import { createContext, createEffect, createMemo, createSignal, For, onCleanup, onMount, Show, splitProps, useContext, type Accessor, type JSX, type ParentProps } from "solid-js"
import { Title } from "@solidjs/meta"
import { useLocation, useSearchParams } from "@solidjs/router"
import { Button } from "@mux/ui/button"
import { Icon } from "@mux/ui/icon"
import { ControlPageBack, MobileControlPageBack } from "@/components/control-page-back"
import { useNavigate } from "@/utils/navigation"

/**
 * Shared frame and building blocks for control-plane pages (Machines,
 * Providers, Usage, Capabilities, Settings). The page owns one scroll
 * container with a stable gutter, so content that grows or loads never shifts
 * the layout sideways or resets the reader's position.
 *
 * Layout follows the room the page has, not the screen: a Grid Mode window on
 * a desktop can be narrower than a phone. The page is the `control-page`
 * container (section navigation beside or above the content) and its scroller
 * is the `control-content` container (row layouts, spacing, and type size).
 * `md:` still means the desktop device, so phones keep their touch layout.
 */

const pageWidth = { default: "max-w-[800px]", wide: "max-w-[1180px]" } as const

const ControlScrollerContext = createContext<Accessor<HTMLElement | undefined>>(() => undefined)

/** The page's scroll container, for content that scrolls it, such as drag auto-scroll. */
export const useControlScroller = () => useContext(ControlScrollerContext)

/** A row or section revealed by `?focus=` flashes so the eye lands on it. */
const focusHighlight = "transition-colors duration-700 data-[control-focus]:bg-surface-raised-base"
const FOCUS_HIGHLIGHT_MS = 2000
let revealRequests = 0

/**
 * Navigation options for a `?focus=` link that reveal its row again even when
 * the link is already the current URL; the router ignores a navigation that
 * changes neither URL nor state.
 */
export function controlFocusNavigation() {
  return { state: { controlFocusRequest: ++revealRequests } }
}

function revealControlAnchor(container: HTMLElement, anchor: string) {
  let timer: ReturnType<typeof setTimeout> | undefined
  let target: HTMLElement | undefined
  // Wait a frame so the section that owns the anchor has rendered.
  const frame = requestAnimationFrame(() => {
    target = [...container.querySelectorAll<HTMLElement>("[data-control-anchor]")].find((element) => element.dataset.controlAnchor === anchor)
    if (!target) return
    target.scrollIntoView({ block: "center" })
    target.setAttribute("data-control-focus", "")
    timer = setTimeout(() => target?.removeAttribute("data-control-focus"), FOCUS_HIGHLIGHT_MS)
  })
  onCleanup(() => {
    cancelAnimationFrame(frame)
    if (timer !== undefined) clearTimeout(timer)
    target?.removeAttribute("data-control-focus")
  })
}

export function ControlPage(props: ParentProps<{
  title: string
  /** Document title when the heading is not the page name (Settings sections). */
  documentTitle?: string
  description?: JSX.Element
  actions?: JSX.Element
  width?: keyof typeof pageWidth
  /** Section navigation; replaces the mobile back link, which it must provide. */
  nav?: JSX.Element
  /** The mobile back link to the dashboard's chats; off on pages outside the dashboard. */
  back?: boolean
  scrollRef?: (element: HTMLDivElement) => void
}>) {
  const [scroller, setScroller] = createSignal<HTMLDivElement>()
  return (
    <div class="@container/control-page size-full min-h-0 min-w-0 bg-background-base">
      <div data-component="control-page" class="flex size-full min-h-0 min-w-0 flex-col pt-[env(safe-area-inset-top)] md:pt-0 md:@3xl/control-page:flex-row">
        <Title>{`${props.documentTitle ?? props.title} · Kettle`}</Title>
        {props.nav}
        <div
          ref={(element) => { setScroller(element); props.scrollRef?.(element) }}
          class="@container/control-content min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable]"
        >
          <div class={`mx-auto w-full px-5 pb-16 pt-6 @xl/control-content:px-8 md:@xl/control-content:pt-10 ${pageWidth[props.width ?? "default"]}`}>
            <Show when={!props.nav && props.back !== false}><MobileControlPageBack /></Show>
            {/* Wraps, never squeezes: the title's basis moves actions under it when both do not fit. */}
            <header class="mb-6 flex flex-wrap items-start justify-between gap-4 @xl/control-content:mb-8">
              <div class="min-w-0 flex-[1_1_16rem]">
                <h1 class="text-18-medium text-text-strong @xl/control-content:text-20-medium">{props.title}</h1>
                <Show when={props.description}><p class="mt-1 max-w-2xl text-12-regular text-text-weak @xl/control-content:text-13-regular">{props.description}</p></Show>
              </div>
              <Show when={props.actions}><div class="flex max-w-full shrink-0 flex-wrap items-center gap-2">{props.actions}</div></Show>
            </header>
            <div class="flex flex-col gap-8 @xl/control-content:gap-10">
              <ControlScrollerContext.Provider value={scroller}>{props.children}</ControlScrollerContext.Provider>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function ControlSection(props: ParentProps<{
  title?: JSX.Element
  description?: JSX.Element
  actions?: JSX.Element
  labelledBy?: string
  /** Search destination ID; see `useControlPageSection`. */
  anchor?: string
}>) {
  return (
    <section data-control-anchor={props.anchor} class={`flex min-w-0 flex-col gap-4 ${focusHighlight}`} aria-labelledby={props.labelledBy}>
      <Show when={props.title || props.actions}>
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0 flex-[1_1_14rem]">
            <Show when={props.title}><h2 id={props.labelledBy} class="text-15-medium text-text-strong @xl/control-content:text-16-medium">{props.title}</h2></Show>
            <Show when={props.description}><p class="mt-1 text-12-regular text-text-weak @xl/control-content:text-13-regular">{props.description}</p></Show>
          </div>
          <Show when={props.actions}><div class="flex max-w-full shrink-0 flex-wrap items-center gap-2">{props.actions}</div></Show>
        </div>
      </Show>
      {props.children}
    </section>
  )
}

/** Bordered surface for free-form content. */
export function ControlPanel(props: ParentProps<JSX.HTMLAttributes<HTMLDivElement>>) {
  const [local, rest] = splitProps(props, ["class", "children"])
  return <div {...rest} class={`rounded-xl border border-border-weak-base bg-surface-base ${local.class ?? ""}`}>{local.children}</div>
}

/** Divided list of setting-style rows. */
export function ControlList(props: ParentProps) {
  return <div class="rounded-xl border border-border-weak-base bg-surface-base px-3 @xl/control-content:px-4">{props.children}</div>
}

/** Makes custom content a `?focus=` destination, like a row or section `anchor`. */
export function ControlAnchor(props: ParentProps<{ anchor: string }>) {
  return <div data-control-anchor={props.anchor} class={focusHighlight}>{props.children}</div>
}

/**
 * A setting with its control on the right. When the row cannot fit both, the
 * control moves under the text instead of squeezing the title to a sliver.
 */
export function ControlRow(props: ParentProps<{ title: JSX.Element; description?: JSX.Element; anchor?: string }>) {
  return (
    <div data-control-anchor={props.anchor} class={`flex min-h-16 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border-weak-base py-3 last:border-none @xl/control-content:gap-x-4 ${focusHighlight}`}>
      <div class="flex min-w-0 flex-[1_1_14rem] flex-col gap-0.5">
        <span class="text-13-medium text-text-strong @xl/control-content:text-14-medium">{props.title}</span>
        <Show when={props.description}><span class="text-11-regular leading-4 text-text-weak @xl/control-content:text-12-regular">{props.description}</span></Show>
      </div>
      <div class="ml-auto flex min-w-0 max-w-full shrink-0 justify-end">{props.children}</div>
    </div>
  )
}

const noticeTone = {
  neutral: "border-border-weak-base bg-surface-base text-text-weak",
  warning: "border-icon-warning-base/30 bg-icon-warning-base/5 text-text-base",
  danger: "border-danger-base bg-danger-muted text-text-strong",
} as const

/** Inline status sized like content, so it never reflows the page around it. */
export function ControlNotice(props: ParentProps<{
  tone?: keyof typeof noticeTone
  /** Announce changes; use for loading and progress messages. */
  live?: boolean
  action?: { label: string; onClick: () => void; disabled?: boolean }
}>) {
  const tone = () => props.tone ?? "neutral"
  return (
    <div
      role={tone() === "danger" ? "alert" : props.live ? "status" : undefined}
      class={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-13-regular ${noticeTone[tone()]}`}
    >
      <div class="min-w-0 break-words">{props.children}</div>
      <Show when={props.action}>
        {(action) => <Button size="small" variant="secondary" disabled={action().disabled} onClick={() => action().onClick()}>{action().label}</Button>}
      </Show>
    </div>
  )
}

/** Height-animated container; hidden content is inert and out of the tab order. */
export function ControlDisclosure(props: ParentProps<{ open: boolean }>) {
  return (
    <div
      class="grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none"
      style={{ "grid-template-rows": props.open ? "1fr" : "0fr" }}
      aria-hidden={!props.open}
      inert={!props.open}
    >
      <div class="min-h-0 overflow-hidden">{props.children}</div>
    </div>
  )
}

/** Caret for a disclosure trigger: points right while closed and down while open. */
export function ControlDisclosureChevron(props: { open: boolean; class?: string }) {
  return (
    <span
      aria-hidden="true"
      class={`flex shrink-0 transition-transform duration-200 motion-reduce:transition-none ${props.class ?? ""}`}
      classList={{ "-rotate-90": !props.open }}
    >
      <Icon name="chevron-down" size="small" />
    </span>
  )
}

export type ControlPageSection = { id: string; title: string }

/**
 * Active section of a sectioned control page, kept in `?section=` so links and
 * reloads land on it. Unknown IDs fall back to the first section. Each section
 * switch is its own history entry.
 * `?focus=` names a row or section `anchor` to scroll to and briefly highlight.
 */
export function useControlPageSection<T extends ControlPageSection>(sections: readonly T[]) {
  const location = useLocation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const active = createMemo(() => {
    const id = typeof params.section === "string" ? params.section : undefined
    return sections.find((section) => section.id === id) ?? sections[0]
  })
  const focus = () => (typeof params.focus === "string" ? params.focus : undefined)
  let content: HTMLDivElement | undefined
  createEffect(() => {
    active()
    const anchor = focus()
    // Each navigation carries its own state, so following the same focus link
    // again reveals its row again.
    location.state
    if (!content) return
    content.scrollTop = 0
    if (anchor) revealControlAnchor(content, anchor)
  })
  const href = (id: string) => {
    const query = new URLSearchParams(location.search)
    query.set("section", id)
    query.delete("focus")
    return `${location.pathname}?${query}`
  }
  return {
    active,
    href,
    select: (id: string) => navigate(href(id), { scroll: false }),
    scrollRef: (element: HTMLDivElement) => { content = element },
  }
}

/** Section navigation for `ControlPage.nav`: links beside a wide page, a picker above a narrow one. Children render at the bottom. */
export function ControlSectionNav(props: ParentProps<{
  /** Page name, used for accessible labels such as "Settings sections". */
  label: string
  sections: readonly ControlPageSection[]
  active: string
  href: (id: string) => string
  onSelect: (id: string) => void
}>) {
  return (
    <aside class="flex shrink-0 flex-col gap-3 border-b border-border-weak-base px-4 py-3 md:@3xl/control-page:w-44 md:@3xl/control-page:gap-4 md:@3xl/control-page:overflow-y-auto md:@3xl/control-page:border-r md:@3xl/control-page:border-b-0 md:@3xl/control-page:px-3 md:@3xl/control-page:py-4">
      <ControlPageBack />
      <nav aria-label={`${props.label} sections`} class="hidden flex-col gap-px md:@3xl/control-page:flex">
        <For each={props.sections}>
          {(section) => (
            <a
              href={props.href(section.id)}
              aria-current={props.active === section.id ? "page" : undefined}
              class="flex h-7 items-center rounded-md px-2 text-13-regular transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              classList={{
                "bg-surface-raised-base-active text-13-medium text-text-strong": props.active === section.id,
                "text-text-weak hover:bg-surface-raised-base hover:text-text-strong": props.active !== section.id,
              }}
            >
              {section.title}
            </a>
          )}
        </For>
      </nav>
      <select
        aria-label={`${props.label} section`}
        class="min-h-11 w-full rounded-lg border border-border-weak-base bg-background-base px-3 text-14-medium text-text-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus md:min-h-8 md:text-13-medium md:@3xl/control-page:hidden"
        value={props.active}
        onChange={(event) => props.onSelect(event.currentTarget.value)}
      >
        <For each={props.sections}>{(section) => <option value={section.id}>{section.title}</option>}</For>
      </select>
      {props.children}
    </aside>
  )
}

/** Route-chunk fallback with the same frame as the page it stands in for. */
export function ControlPageSkeleton(props: { title: string; width?: keyof typeof pageWidth }) {
  // Fast chunk loads should not flash placeholder content.
  const [visible, setVisible] = createSignal(false)
  onMount(() => {
    const timer = window.setTimeout(() => setVisible(true), 140)
    onCleanup(() => window.clearTimeout(timer))
  })
  return (
    <ControlPage title={props.title} width={props.width}>
      <div class="flex flex-col gap-10 transition-opacity duration-200" classList={{ "opacity-0": !visible() }} aria-hidden="true">
        {[2, 3].map((rows) => (
          <div class="flex flex-col gap-4">
            <div class="h-4 w-36 rounded bg-surface-raised-base/60" />
            {Array.from({ length: rows }, () => <div class="h-16 rounded-xl border border-border-weak-base bg-surface-base" />)}
          </div>
        ))}
      </div>
    </ControlPage>
  )
}
